import { Box } from '@mui/material';
import { Typography, colors } from '@sweep-io/sweep-design';
import { AvatarPicture } from '../../avatar/AvatarPicture';
import { DIMENSION_AVATAR_SMALL, DIMENSION_AVATAR_VERY_SMALL } from '../../../constants';
import UserSummaryBasic from './UserSummaryBasic';
import { UserInfo } from '../../../types/UserInfoTypes';
import { RolesDropdown } from './RolesDropdown';
import { getIsUserInactive } from '../../../lib/getIsUserActive';
import { UserDefinedRoleGroupsInterface } from '@server/role-group-interface';
import { useFeatureToggle } from '../../../feature-flags/useFeatureToggle';
import { useState } from 'react';

export interface UserSummaryNewProps {
  user?: UserInfo;
  roles: UserDefinedRoleGroupsInterface[];
  onChangeRoleCb: (userId: string, newRole: string) => void;
  disableRoleDropdown?: boolean;
  onUserRemove?: (userId: string) => void;
  showBorders?: boolean;
}

const UserSummaryNew = ({
  user = {},
  roles,
  onChangeRoleCb,
  onUserRemove,
  disableRoleDropdown,
  showBorders,
}: UserSummaryNewProps) => {
  const { multiAccounts } = useFeatureToggle();
  const { name, email, roleGroupId: role, id, status } = user || {};
  const _userRole = roles.find((_role) => _role.id === role);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  return (
    <UserSummaryBasic
      className="user-summary"
      data-testid="user-summary"
      key={id}
      sx={{
        borderBottom: multiAccounts ? `1px solid ${colors.grey[120]}` : undefined,
        alignItems: multiAccounts ? 'center' : undefined,
        '&:hover, &:active': {
          backgroundColor: multiAccounts ? colors.grey[100] : undefined,
        },
        backgroundColor: multiAccounts && isDropdownOpen ? colors.grey[100] : undefined,
        pr: multiAccounts ? '16px' : undefined,
        pl: multiAccounts ? '16px' : undefined,
      }}
    >
      <div className="user-summary-avatar">
        <AvatarPicture
          avatar={{ emoji: user.emoji, imageUrl: user.imageUrl }}
          dimension={multiAccounts ? DIMENSION_AVATAR_SMALL : DIMENSION_AVATAR_VERY_SMALL}
          isInactive={getIsUserInactive(status)}
        />
      </div>
      <Box
        display="flex"
        flexDirection="column"
        overflow="hidden"
        className="text-info"
        gap="4px"
        flex="1 0"
        minHeight={multiAccounts ? '84px' : undefined}
        justifyContent={multiAccounts ? 'center' : undefined}
      >
        <Typography
          data-testid="user-summary-name"
          ellipsis
          variant={multiAccounts ? 'body-medium' : 'body'}
        >
          {name}
        </Typography>
        <Typography
          data-testid="user-summary-email"
          ellipsis
          variant="caption"
          color="text.secondary"
        >
          {email}
        </Typography>
      </Box>

      {user && (
        <RolesDropdown
          onChangeRoleCb={onChangeRoleCb}
          accountRoles={roles}
          disableRoleDropdown={disableRoleDropdown}
          userRole={_userRole}
          userId={id}
          onUserRemove={onUserRemove}
          onOpen={() => setIsDropdownOpen(true)}
          onClose={() => setIsDropdownOpen(false)}
          showBorders={showBorders}
        />
      )}
    </UserSummaryBasic>
  );
};

export { UserSummaryNew };
